<script setup lang="ts">
import { computed, onMounted, ref } from 'vue';
import { useRouter, useRoute } from 'vue-router';
import {
  FhButton,
  FhText,
  FhTextInput,
  FhLabel,
  FhCard,
} from '@fareharbor-com/beacon-vue';
import { postConfirmPasswordReset } from '@/common/api/postConfirmPasswordReset';
import { handleThrownError } from '@/common/errors/handleThrownError';
import { ValidationService } from '@/common/validation';
import DeskForm from '@/components/shared/DeskForm/DeskForm.vue';
import BaseFlexbox from '@/components/ui/BaseFlexbox/BaseFlexbox.vue';
import { config } from '@/configs';
import { useAuthStore } from '@/stores/auth';
import { InventoryEnvironment } from '@/types/InventoryEnvironment';

const currentEnvironment = config.ENVIRONMENT;
const isStagingOrLocal = computed(() =>
  [InventoryEnvironment.STAGING, InventoryEnvironment.LOCAL].includes(
    currentEnvironment,
  ),
);

const router = useRouter();
const route = useRoute();
const authStore = useAuthStore();

// Extract UID & Token from the URL
const uid = ref<string | null>(null);
const token = ref<string | null>(null);
const email = ref<string | null>(null);

// Form Data
const newPassword = ref('');
const confirmPassword = ref('');
const isSubmitting = ref(false);
const successMessage = ref('');
const errorMessages = ref<string[]>([]);

const isValid = computed(() => {
  const validation = ValidationService.validatePassword(newPassword.value);
  return validation.isValid && newPassword.value === confirmPassword.value;
});

onMounted(() => {
  uid.value = (route.query.uid as string) || null;
  token.value = (route.query.token as string) || null;
  email.value = (route.query.email as string) || null;
  const currentRoute = router.currentRoute.value;
  authStore.logout();
  router.push(currentRoute);
});

function validateForm() {
  const validation = ValidationService.validatePasswordReset(
    newPassword.value,
    confirmPassword.value,
  );

  errorMessages.value = validation.errors;
  return validation.isValid;
}

// Function to submit new password
async function confirmPasswordReset() {
  // clear messages & show spinner
  isSubmitting.value = true;
  successMessage.value = '';
  errorMessages.value = [];

  if (!validateForm()) {
    isSubmitting.value = false;
    return;
  }

  try {
    const { status, data, error } = await postConfirmPasswordReset({
      uid: uid.value,
      token: token.value,
      new_password1: newPassword.value,
      new_password2: confirmPassword.value,
    });

    if (error || status >= 400) {
      if (data && typeof data === 'object') {
        errorMessages.value = Object.values(data).flat() as string[];
      } else {
        errorMessages.value = ['An error occurred. Please try again.'];
      }
      return;
    }

    successMessage.value = 'Your password has been successfully reset.';
  } catch (err) {
    handleThrownError({
      error: err,
      toastMessage:
        'An error occurred. Please check your network connection and try again.',
      log: true,
    });
  } finally {
    isSubmitting.value = false;
  }
}

const topBlurClass = computed(() =>
  isStagingOrLocal.value ? 'top-10' : 'top-0',
);

const loginFormHeight = computed(() =>
  isStagingOrLocal.value ? 'calc(100vh - 165px)' : 'calc(100vh - 150px)',
);
</script>

<template>
  <BaseFlexbox :style="{ height: loginFormHeight }">
    <div
      :class="[
        'flex flex-col fixed inset-x-0 z-50 flex justify-center items-center w-full h-screen backdrop-blur-sm ',
        topBlurClass,
      ]"
    >
      <BaseFlexbox
        justifyContent="center"
        class="relative w-full max-w-md p-6 border-gray-300"
      >
        <img
          src="@/assets/images/logo.png"
          alt="Marketplace Logo"
          class="h-14"
        />
      </BaseFlexbox>
      <FhCard
        elevated
        id="confirm-password-reset-modal"
        data-test-id="confirm-password-reset-modal"
        size="lg"
        class="bg-white shadow-lg rounded-lg overflow-hidden w-full max-w-md"
      >
        <div v-if="!successMessage">
          <FhText variant="body-lg" alignment="center" class="mt-1">
            Enter your new password below to complete the reset process.
          </FhText>
        </div>

        <DeskForm
          v-if="!successMessage"
          form-name="Confirm Password Reset Form"
          submit-text="Reset Password"
          :errors="errorMessages"
          :is-submitting="isSubmitting"
          :is-valid="isValid"
          @submit.prevent="confirmPasswordReset"
          data-test-id="confirm-password-reset-form"
        >
          <BaseFlexbox direction="column" gap="3" class="mb-3 mt-4">
            <FhTextInput
              type="password"
              v-model="newPassword"
              autocomplete="new-password"
              data-test-id="confirm-password-new"
              size="md"
            >
              <FhLabel>Password</FhLabel>
            </FhTextInput>
          </BaseFlexbox>

          <BaseFlexbox direction="column" gap="3" class="mb-3 mt-4">
            <FhTextInput
              type="password"
              v-model="confirmPassword"
              autocomplete="new-password"
              data-test-id="confirm-password-confirm"
              size="md"
            >
              <FhLabel>Confirm password</FhLabel>
            </FhTextInput>
          </BaseFlexbox>

          <template #form-actions>
            <FhButton
              :disabled="isSubmitting"
              :loading="isSubmitting"
              type="submit"
              fullWidth
              data-test-id="confirm-password-reset-button"
              class="mt-2"
            >
              Reset password
            </FhButton>
          </template>
        </DeskForm>
        <div
          v-if="successMessage"
          class="flex flex-col items-center justify-center py-10"
        >
          <FhText
            variant="body-lg"
            alignment="center"
            class="font-medium text-gray-900 mb-2"
            style="font-size: 1.5rem"
          >
            Password Changed!
          </FhText>
          <FhText
            variant="body-md"
            alignment="center"
            class="text-gray-600 mb-6"
          >
            Your password has been changed successfully.
          </FhText>
          <FhButton
            variant="primary"
            fullWidth
            @click="router.push({ name: 'login' })"
          >
            Back to Login
          </FhButton>
        </div>
      </FhCard>
    </div>
  </BaseFlexbox>
</template>
